import { useDefaultShiftTimes } from "@src/appV2/redesign/DailyView/components/ShiftPostingForm/hooks/useDefaultShiftTimes";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { useCallback, useRef, useState } from "react";

import { getInitialBookableSlot } from "../../../../DailyView/components/InviteDrawer/hooks/inviteFlowUtils";
import { type ShiftDetails } from "../../../../DailyView/components/InviteDrawer/types";

interface UseChatInviteShiftDetailsParams {
  workplaceId: string;
  qualification: string;
  qualificationDisplayName: string;
  timezone: string;
}

interface UseChatInviteShiftDetailsReturn {
  selectedDate: Date;
  selectedTimeSlot: TimeSlot;
  shiftDetails: ShiftDetails | undefined;
  isLoading: boolean;
  setSelectedDate: (date: Date) => void;
  setSelectedTimeSlot: (slot: TimeSlot) => void;
}

/**
 * Builds a ShiftDetails object for the chat invite flow by combining
 * user-selected date + time slot with fetched workplace shift times.
 *
 * Reuses useDefaultShiftTimes (which calls useGetShiftTimes internally)
 * to fetch the workplace's configured AM/PM/NOC start + duration.
 */
export function useChatInviteShiftDetails(
  params: UseChatInviteShiftDetailsParams
): UseChatInviteShiftDetailsReturn {
  const { workplaceId, qualification, qualificationDisplayName, timezone } = params;

  // Called twice so each useState can have its own initializer; both invocations
  // run during the same mount tick and stay consistent.
  const [selectedDate, setSelectedDate] = useState<Date>(
    () => getInitialBookableSlot(timezone).date
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot>(
    () => getInitialBookableSlot(timezone).slot
  );

  const { defaultTimes, isLoading } = useDefaultShiftTimes({
    workplaceId,
    qualification,
    timeSlot: selectedTimeSlot,
    selectedDate,
    timezone,
    enabled: Boolean(qualification),
  });

  const handleSetSelectedDate = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleSetSelectedTimeSlot = useCallback((slot: TimeSlot) => {
    setSelectedTimeSlot(slot);
  }, []);

  // Cache the last valid times so the dialog doesn't collapse to a "Loading..."
  // state and resize every time the user switches qualifications. The new
  // qualification is applied immediately; only the times are held over.
  const lastValidTimesRef = useRef<{ start: Date; end: Date } | undefined>(undefined);
  if (defaultTimes) {
    lastValidTimesRef.current = defaultTimes;
  }
  const effectiveTimes = defaultTimes ?? lastValidTimesRef.current;

  const shiftDetails: ShiftDetails | undefined = effectiveTimes
    ? {
        startTime: effectiveTimes.start,
        endTime: effectiveTimes.end,
        timeSlot: selectedTimeSlot,
        qualification,
        qualificationDisplayName,
        workplaceId,
        timezone,
        selectedDate,
      }
    : undefined;

  // Only surface loading state on the very first load (no cached times yet).
  // Subsequent refetches happen under the surface so the dialog stays stable.
  const isFirstLoad = !lastValidTimesRef.current;

  return {
    selectedDate,
    selectedTimeSlot,
    shiftDetails,
    isLoading: isLoading && isFirstLoad,
    setSelectedDate: handleSetSelectedDate,
    setSelectedTimeSlot: handleSetSelectedTimeSlot,
  };
}
